import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LockKeyhole, Mail, User } from "lucide-react";
import { checkAuthStatus, login, register, setup } from "../api";
import { useAuth } from "../auth";

export function Login({ initialMode = "login" }: { initialMode?: "login" | "register" }) {
  const navigate = useNavigate();
  const { user, acceptAuth } = useAuth();
  const [mode, setMode] = useState<"login" | "register" | "setup">(initialMode);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [form, setForm] = useState({ username: "", email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (user) navigate(`/users/${user.id}`, { replace: true }); }, [user, navigate]);
  useEffect(() => {
    checkAuthStatus().then((status) => {
      if (!status.hasAdmin) setMode("setup");
      setRegistrationEnabled(status.registrationEnabled);
    }).catch(() => undefined);
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(""); setSubmitting(true);
    try {
      const result = mode === "login"
        ? await login(form.email, form.password)
        : mode === "setup"
          ? await setup(form.username, form.email, form.password, form.confirmPassword)
          : await register(form.username, form.email, form.password, form.confirmPassword);
      acceptAuth(result);
      navigate(`/users/${result.user.id}`, { replace: true });
    } catch (err) { setError(err instanceof Error ? err.message : "操作失败"); }
    finally { setSubmitting(false); }
  };

  const inputClass = "w-full h-11 pl-11 pr-4 rounded-xl bg-[var(--bg-hover)] border border-[var(--border-default)] outline-none focus:border-primary text-[var(--text-primary)]";
  return <div className="max-w-md mx-auto px-6 py-20">
    <div className="rounded-3xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-7 sm:p-9 shadow-xl">
      <h1 className="text-3xl font-bold text-center mb-2">{mode === "login" ? "欢迎回来" : mode === "setup" ? "初始化管理员" : "创建账号"}</h1>
      <p className="text-center text-sm text-[var(--text-secondary)] mb-8">{mode === "setup" ? "首次运行需要先创建管理员账号" : "登录后即可发布和管理 Markdown 文章"}</p>
      <form onSubmit={submit} className="space-y-4">
        {mode !== "login" && <label className="relative block"><User className="absolute left-3.5 top-3.5 w-4 h-4 text-[var(--text-tertiary)]"/><input className={inputClass} placeholder="用户名" value={form.username} onChange={e => setForm({...form, username:e.target.value})} minLength={2} required /></label>}
        <label className="relative block"><Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-[var(--text-tertiary)]"/><input className={inputClass} type="email" placeholder="邮箱" value={form.email} onChange={e => setForm({...form, email:e.target.value})} required /></label>
        <label className="relative block"><LockKeyhole className="absolute left-3.5 top-3.5 w-4 h-4 text-[var(--text-tertiary)]"/><input className={inputClass} type="password" placeholder="密码（至少 6 位）" value={form.password} onChange={e => setForm({...form, password:e.target.value})} minLength={6} required /></label>
        {mode !== "login" && <label className="relative block"><LockKeyhole className="absolute left-3.5 top-3.5 w-4 h-4 text-[var(--text-tertiary)]"/><input className={inputClass} type="password" placeholder="确认密码" value={form.confirmPassword} onChange={e => setForm({...form, confirmPassword:e.target.value})} minLength={6} required /></label>}
        {error && <p className="text-sm text-rose bg-rose/10 rounded-lg px-3 py-2">{error}</p>}
        <button disabled={submitting} className="w-full h-11 rounded-xl text-white font-semibold bg-gradient-to-r from-primary to-accent disabled:opacity-60 flex items-center justify-center gap-2">{submitting && <Loader2 className="w-4 h-4 animate-spin"/>}{mode === "login" ? "登录" : mode === "setup" ? "创建管理员" : "注册"}</button>
      </form>
      {mode !== "setup" && <div className="text-center text-sm text-[var(--text-secondary)] mt-6">
        {mode === "login" ? <>还没有账号？ {registrationEnabled ? <Link className="text-primary-light" to="/register" onClick={() => setMode("register")}>立即注册</Link> : "注册暂未开放"}</> : <>已有账号？ <Link className="text-primary-light" to="/login" onClick={() => setMode("login")}>去登录</Link></>}
      </div>}
    </div>
  </div>;
}
